import { toLocalMidnightForCalendarDate } from './date';

/**
 * How far BACK an employee may reach on Individual Time Entry to view and edit their own days.
 *
 * WI 31928 (Tim, 2026-09-08): a foreman must be able to edit the last few weeks of their time,
 * not just the previous seven days. The old inline minus-seven-day bound stranded any day older
 * than a week, so late-in-the-month corrections fell off the picker. Four weeks matches the
 * forward leave-advance span, making the picker window symmetric (today - 4wk .. today + 4wk).
 *
 * Scope: this file owns ONLY the past/backdate side. The forward leave-advance bound is a
 * separate rule and lives in `foreman-leave-advance-window.ts` ({@link maxLeaveEntryDate}). The
 * two bounds are INDEPENDENT constants on purpose: widening one must never silently move the
 * other. Past days carry no per-day payroll or exported lock on the ITE page, so this bound
 * governs ordinary retroactive editing and is NOT a leave-only region.
 */
export const FOREMAN_ENTRY_BACKDATE_WEEKS = 4;

/** Four work weeks expressed in calendar days. */
export const FOREMAN_ENTRY_BACKDATE_DAYS = FOREMAN_ENTRY_BACKDATE_WEEKS * 7;

/**
 * The earliest calendar day an employee may select, as a local midnight.
 *
 * 🔴 Calendar arithmetic (`setDate`), never `getTime() - n * 86400000`. Millisecond math across
 * a DST boundary lands an hour off and can resolve to the WRONG calendar day, an off-by-one
 * bound that only misbehaves twice a year. Counting BACKWARD, the trap is the spring-forward
 * boundary (the forward twin's trap is fall-back, because it counts the other way). The old
 * inline `Date.now() - 7 * 24 * 60 * 60 * 1000` minDate on both ITE pages carried exactly that
 * latent defect; pointing them here removes it.
 */
export function minEntryDate(today: Date = new Date()): Date {
  // `toLocalMidnightForCalendarDate` returns null for an invalid Date. Fall back to the current
  // day rather than propagating null: this value bounds a picker, and a null min would render as
  // "no lower bound", silently removing the very window this rule creates.
  const base =
    toLocalMidnightForCalendarDate(today) ??
    toLocalMidnightForCalendarDate(new Date()) ??
    new Date();
  base.setDate(base.getDate() - FOREMAN_ENTRY_BACKDATE_DAYS);
  return base;
}
